import { getUserLotShare, shareUserLot } from '../services/userLotsShareService';
import { getMyCadParcels, updateCadProject } from '../services/cadService';
import { normalizeShareBrandId, normalizeShareTemplate, readShareApiError } from '../Map/utils/sharedBranding';

/*
 * Crear campaña sin link público (decisión de Ignacio, t1098u-t1099u). Hoy el anuncio lleva al link de
 * compartir del registro (FRONTEND_META_CREAR.md §4.0): la unidad → `/#/shared-user-lot/{token}` (share
 * del lote), la parcela → el showroom público. Si no hay share (`not_shared`) o pide login
 * (`share_restricted`), el modal de crear ofrece arreglarlo en un paso con el share que ya existe:
 * - unidad sin share → `POST /shareuserlot` con solo esa unidad, `public` (compat `user_lot_id`);
 * - unidad con share privado → el mismo share (unidades, plantilla y marca de antes) + esta unidad, `public`;
 * - parcela → `PATCH /updatecadproject` del showroom con `is_shared: true` + `access_mode: public`.
 * Solo el dueño en su espacio llega a este modal (`metaCreateAccess`): nada va con `owner_user_id`.
 *
 * «Compartido para el anuncio»: cuando el share lo crea este paso (no existía), se anota en este
 * navegador (localStorage). Si después se elimina la última campaña de Terrio que lo usa, se ofrece
 * dejar de compartir (`metaShareGuard`). Abrir el diálogo de compartir y guardar / apagar lo borra
 * (ya es una decisión del dueño). La API no guarda este dato: ver la pregunta para APIBOT en las docs.
 */

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const idStr = (v) => (v == null ? null : str(String(v)));

/** Motivos de `blocked_reason` que el modal resuelve compartiendo. */
export const META_CREATE_SHARE_REASONS = ['not_shared', 'share_restricted'];

/**
 * Qué ofrece el modal con `not_shared` / `share_restricted` (`null` con cualquier otro motivo).
 * @returns {{ reason: string, text: string, actionLabel: string }|null}
 */
export function metaCreateShareFix(reason, itemKind = 'user_lot') {
  const showroom = itemKind === 'cad_parcel';
  if (reason === 'not_shared') {
    return {
      reason,
      text: showroom
        ? 'El anuncio lleva a una página pública. Para crearla, compartimos este showroom con un link abierto.'
        : 'El anuncio lleva a una página pública. Para crearla, compartimos esta unidad con un link abierto.',
      actionLabel: 'Compartir y seguir',
    };
  }
  if (reason === 'share_restricted') {
    return {
      reason,
      text: showroom
        ? 'El link del showroom pide iniciar sesión y quien toque el anuncio no lo vería. Pasalo a público para seguir.'
        : 'El link de esta unidad pide iniciar sesión y quien toque el anuncio no la vería. Pasalo a público para seguir.',
      actionLabel: 'Pasar a público y seguir',
    };
  }
  return null;
}

/** Toast después de compartir desde el modal. */
export function metaCreateSharedMessage(reason, itemKind = 'user_lot') {
  const what = itemKind === 'cad_parcel' ? 'el showroom' : 'la unidad';
  return reason === 'share_restricted'
    ? `Listo: ${what} ya tiene un link público.`
    : `Listo: compartimos ${what} con un link público.`;
}

export const META_CREATE_SHARE_ERROR = 'No pudimos compartir. Probá de nuevo.';

// ── «Compartido para el anuncio» (localStorage, este navegador) ─────────────

const AUTO_SHARE_KEY = 'terrio.metaAutoShare';

const localStore = {
  get() {
    try { return window.localStorage.getItem(AUTO_SHARE_KEY); } catch { return null; }
  },
  set(value) {
    try { window.localStorage.setItem(AUTO_SHARE_KEY, value); } catch { /* sin storage: no se anota */ }
  },
};

/** Entradas `{ scope: 'lot'|'project', id, recordIds: string[], sharedAt }`. */
function readAll() {
  try {
    const list = JSON.parse(localStore.get() || '[]');
    return Array.isArray(list) ? list.filter((e) => e && (e.scope === 'lot' || e.scope === 'project') && str(e.id)) : [];
  } catch {
    return [];
  }
}
const writeAll = (list) => localStore.set(JSON.stringify(list));
const sameId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/** Anota que el share de este lote / showroom lo creó «Crear campaña». */
export function rememberMetaAutoShare({ scope, id, recordId }) {
  const sid = idStr(id);
  if (!sid || (scope !== 'lot' && scope !== 'project')) return;
  const list = readAll().filter((e) => !(e.scope === scope && sameId(e.id, sid)));
  list.push({ scope, id: sid, recordIds: recordId ? [String(recordId)] : [], sharedAt: Date.now() });
  writeAll(list);
}

/** El dueño tocó el share a mano (guardar o apagar): deja de ser «solo para el anuncio». */
export function forgetMetaAutoShare({ scope, id }) {
  const sid = idStr(id);
  if (!sid) return;
  const list = readAll();
  const next = list.filter((e) => !(e.scope === scope && sameId(e.id, sid)));
  if (next.length !== list.length) writeAll(next);
}

/** Entrada del share del registro (`user_lot` por unidad, `cad_parcel` por showroom) o `null`. */
export function findMetaAutoShare({ itemKind, id, projectId = null }) {
  const list = readAll();
  if (itemKind === 'cad_parcel') {
    return list.find((e) => e.scope === 'project'
      && ((projectId && sameId(e.id, projectId)) || e.recordIds.some((r) => sameId(r, id)))) || null;
  }
  if (itemKind === 'user_lot') return list.find((e) => e.scope === 'lot' && e.recordIds.some((r) => sameId(r, id))) || null;
  return null;
}

// ── Compartir desde el modal ─────────────────────────────────────────────────

/** Showroom de una parcela: el que vino por props o el de `getmycadparcels` (`project_id`). */
export async function resolveMetaParcelProjectId(parcelId, projectId = null) {
  if (idStr(projectId)) return idStr(projectId);
  const res = await getMyCadParcels();
  if (!res?.ok) return null;
  const row = (res.parcels || []).find((p) => sameId(p?.id, parcelId));
  return idStr(row?.project_id);
}

async function shareUnit(recordId, reason) {
  if (reason === 'share_restricted') {
    const current = await getUserLotShare({ userLotId: recordId });
    const data = current?.data || current || {};
    const lotId = idStr(data.lot_id);
    if (!current?._httpOk || !lotId) return { ok: false, message: readShareApiError(current, META_CREATE_SHARE_ERROR) };
    const ids = Array.isArray(data.user_lot_ids) ? data.user_lot_ids.map(String) : [];
    const template = normalizeShareTemplate(data.share_template);
    // Mismo share (unidades, plantilla y marca), ahora abierto y con esta unidad adentro.
    const res = await shareUserLot({
      lot_id: lotId,
      user_lot_ids: ids.some((x) => sameId(x, recordId)) ? ids : [...ids, String(recordId)],
      scope: str(data.scope) || 'all',
      access_mode: 'public',
      share_template: template,
      brand_id: normalizeShareBrandId(data.brand_id),
    });
    if (!res?._httpOk || !res?.data?.share_token) return { ok: false, message: readShareApiError(res, META_CREATE_SHARE_ERROR) };
    return { ok: true, scope: 'lot', id: lotId, created: false };
  }
  // Sin share: solo esta unidad (lo mínimo para el anuncio).
  const res = await shareUserLot({ user_lot_id: String(recordId), scope: 'all', access_mode: 'public' });
  if (!res?._httpOk || !res?.data?.share_token) return { ok: false, message: readShareApiError(res, META_CREATE_SHARE_ERROR) };
  return { ok: true, scope: 'lot', id: idStr(res.data.lot_id), created: true };
}

async function shareShowroom(parcelId, reason, projectId) {
  const id = await resolveMetaParcelProjectId(parcelId, projectId);
  if (!id) return { ok: false, message: META_CREATE_SHARE_ERROR };
  // PATCH parcial: marca, catastro de fondo y personas quedan como estaban.
  const res = await updateCadProject({ id, is_shared: true, access_mode: 'public' });
  if (!res?.ok) return { ok: false, message: str(res?.message) || META_CREATE_SHARE_ERROR };
  return { ok: true, scope: 'project', id, created: reason === 'not_shared' };
}

/**
 * Comparte el registro para poder crear (`not_shared` / `share_restricted`). Si el share es nuevo, lo
 * anota como «compartido para el anuncio».
 * @returns {Promise<{ ok: boolean, message?: string, scope?: 'lot'|'project', id?: string|null, created?: boolean }>}
 */
export async function shareMetaCreateRecord({ itemKind, recordId, reason, projectId = null }) {
  if (!META_CREATE_SHARE_REASONS.includes(reason) || !recordId) return { ok: false, message: META_CREATE_SHARE_ERROR };
  let out;
  try {
    out = itemKind === 'cad_parcel'
      ? await shareShowroom(recordId, reason, projectId)
      : await shareUnit(recordId, reason);
  } catch {
    return { ok: false, message: META_CREATE_SHARE_ERROR };
  }
  if (out.ok && out.created && out.id) rememberMetaAutoShare({ scope: out.scope, id: out.id, recordId });
  return out;
}
